import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  useNotificationSettingsDiscovery,
  useCreateNotificationConfig,
  useUpdateNotificationConfig,
} from "@/hooks/api/useNotifications";
import type {
  NotificationConfig,
  NotificationConfigCreate,
  NotificationConfigUpdate,
  NotificationChannel,
  ComparisonOperator,
  DiscoveryGeographicNode,
} from "@/api/types/notifications";
import { toast } from "sonner";

// Zod schema for form validation
const formSchema = z.object({
  storm_impact_model_id: z.string().min(1, "Storm impact model is required"),
  weather_model_id: z.string().min(1, "Weather model is required"),
  geographic_node_id: z.string().min(1, "Geographic location is required"),
  threshold_variable_slug: z.string().min(1, "Variable is required"),
  comparison_operator: z.enum([">", "<", ">=", "<=", "==", "!="]),
  threshold_value: z.coerce
    .number<number>()
    .min(0, "Threshold must be a positive number"),
  channels: z
    .array(z.enum(["email", "in_app"]))
    .min(1, "Select at least one channel"),
});

// Human-readable labels for comparison operators
const operatorLabels: Record<ComparisonOperator, string> = {
  ">": "greater than",
  "<": "less than",
  ">=": "greater than or equal to",
  "<=": "less than or equal to",
  "==": "equal to",
  "!=": "not equal to",
};

type FormValues = z.infer<typeof formSchema>;

interface NotificationConfigFormProps {
  config?: NotificationConfig; // If provided, form is in edit mode
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function NotificationConfigForm({
  config,
  onSuccess,
  onCancel,
}: NotificationConfigFormProps) {
  const isEditMode = !!config;
  const initialModelIdRef = useRef<string | null>(
    config?.storm_impact_model_id || null
  );

  // Fetch all dropdown options from discovery endpoint (single API call)
  const { data: discoveryData, loading: discoveryLoading } =
    useNotificationSettingsDiscovery();

  // Mutations
  const { execute: createConfig, loading: creating } =
    useCreateNotificationConfig();
  const { execute: updateConfig, loading: updating } =
    useUpdateNotificationConfig();

  // Form setup
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      storm_impact_model_id: config?.storm_impact_model_id || "",
      weather_model_id: config?.weather_model_id || "",
      geographic_node_id: config?.geographic_node_id || "",
      threshold_variable_slug: config?.threshold_variable_slug || "",
      comparison_operator: config?.comparison_operator || ">",
      threshold_value: config?.threshold_value || 0,
      channels: (config?.channels as ("email" | "in_app")[]) || [
        "email",
        "in_app",
      ],
    },
  });

  const selectedModelId = form.watch("storm_impact_model_id");
  const selectedVariableSlug = form.watch("threshold_variable_slug");
  const selectedOperator = form.watch("comparison_operator");
  const thresholdValue = form.watch("threshold_value");
  const selectedGeoNodeId = form.watch("geographic_node_id");

  // Get storm impact models from discovery

  // NOTE: do not include `HIP Research` model
  const stormModels = discoveryData?.storm_impact_models.filter(
    (model) => model.name !== "HIP Research"
  ) || [];

  // Get selected storm model
  const selectedStormModel = useMemo(() => {
    if (!stormModels.length || !selectedModelId) return null;
    return stormModels.find((m) => m.id === selectedModelId) || null;
  }, [stormModels, selectedModelId]);

  // Weather models come from the selected storm model in discovery response
  const weatherModels = selectedStormModel?.weather_models || [];

  // Variables come from the selected storm model in discovery response
  const variables = selectedStormModel?.variables || [];

  // Comparison operators from discovery
  const comparisonOperators = discoveryData?.comparison_operators || [
    ">",
    "<",
    ">=",
    "<=",
    "==",
    "!=",
  ];

  // Flatten geographic hierarchy into selectable nodes
  const geographicNodes = useMemo(() => {
    if (!discoveryData?.geographic_hierarchies) return [];

    const nodes: Array<{
      id: string;
      name: string;
      resolution: string;
      indent: number;
    }> = [];

    const flattenNodes = (
      nodeList: DiscoveryGeographicNode[],
      indent: number = 0
    ) => {
      for (const node of nodeList) {
        nodes.push({
          id: node.id,
          name: node.name,
          resolution: node.resolution,
          indent,
        });
        if (node.trees && node.trees.length > 0) {
          flattenNodes(node.trees, indent + 1);
        }
      }
    };

    for (const hierarchy of discoveryData.geographic_hierarchies) {
      if (hierarchy?.trees) {
        flattenNodes(hierarchy.trees);
      }
    }

    return nodes;
  }, [discoveryData]);

  // Get names for the alert summary
  const selectedVariableName = useMemo(() => {
    if (!selectedVariableSlug || !variables.length) return null;
    const variable = variables.find((v) => v.slug === selectedVariableSlug);
    return variable?.name || selectedVariableSlug;
  }, [selectedVariableSlug, variables]);

  const selectedGeoNodeName = useMemo(() => {
    if (!selectedGeoNodeId || !geographicNodes.length) return null;
    const node = geographicNodes.find((n) => n.id === selectedGeoNodeId);
    return node?.name || null;
  }, [selectedGeoNodeId, geographicNodes]);

  // Reset weather model and variable when storm model changes (but not on initial load for edit mode)
  useEffect(() => {
    if (selectedModelId && selectedModelId !== initialModelIdRef.current) {
      form.setValue("weather_model_id", "");
      form.setValue("threshold_variable_slug", "");
    }
    // Update ref after first change
    if (
      initialModelIdRef.current &&
      selectedModelId !== initialModelIdRef.current
    ) {
      initialModelIdRef.current = null;
    }
  }, [selectedModelId, form]);

  const onSubmit = async (values: FormValues) => {
    if (isEditMode && config) {
      // Update existing config
      const payload: NotificationConfigUpdate = {
        storm_impact_model_id: values.storm_impact_model_id,
        weather_model_id: values.weather_model_id,
        geographic_node_id: values.geographic_node_id,
        threshold_variable_slug: values.threshold_variable_slug,
        comparison_operator: values.comparison_operator as ComparisonOperator,
        threshold_value: values.threshold_value,
        channels: values.channels as NotificationChannel[],
      };

      const result = await updateConfig(config.id, payload);

      if (result) {
        toast.success("Alert configuration updated successfully");
        onSuccess?.();
      } else {
        toast.error("Failed to update alert configuration");
      }
    } else {
      // Create new config
      const payload: NotificationConfigCreate = {
        storm_impact_model_id: values.storm_impact_model_id,
        weather_model_id: values.weather_model_id,
        geographic_node_id: values.geographic_node_id,
        threshold_variable_slug: values.threshold_variable_slug,
        comparison_operator: values.comparison_operator as ComparisonOperator,
        threshold_value: values.threshold_value,
        channels: values.channels as NotificationChannel[],
      };

      const result = await createConfig(payload);

      if (result) {
        toast.success("Alert configuration created successfully");
        form.reset();
        onSuccess?.();
      } else {
        toast.error("Failed to create alert configuration");
      }
    }
  };

  const isSaving = creating || updating;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Geographic Location */}
        <FormField
          control={form.control}
          name="geographic_node_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Geographic Location</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={discoveryLoading}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select location..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {geographicNodes.map((node) => (
                    <SelectItem key={node.id} value={node.id}>
                      <span style={{ paddingLeft: `${node.indent * 12}px` }}>
                        {node.name}
                        <span className="text-muted-foreground ml-2 text-xs">
                          ({node.resolution})
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                Select the specific geographic level you want to monitor
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Storm Impact Model */}
        <FormField
          control={form.control}
          name="storm_impact_model_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Storm Impact Model</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={discoveryLoading}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select model..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {stormModels.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Weather Model */}
        <FormField
          control={form.control}
          name="weather_model_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Weather Model</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={!selectedModelId || weatherModels.length === 0}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select weather model..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {weatherModels.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Variable */}
        <FormField
          control={form.control}
          name="threshold_variable_slug"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Variable to Monitor</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={!selectedModelId || variables.length === 0}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select variable..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {variables.map((variable) => (
                    <SelectItem key={variable.slug} value={variable.slug}>
                      {variable.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Comparison Operator and Threshold Value - Side by side */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="comparison_operator"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Condition</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select operator..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {comparisonOperators.map((op) => (
                      <SelectItem key={op} value={op}>
                        <span className="font-mono">{op}</span>
                        <span className="text-muted-foreground ml-2 text-xs">
                          ({operatorLabels[op as ComparisonOperator] || op})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="threshold_value"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Threshold Value</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="e.g., 10000"
                    {...field}
                    onChange={(e) =>
                      field.onChange(e.target.valueAsNumber || 0)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Notification Channels */}
        <FormField
          control={form.control}
          name="channels"
          render={() => (
            <FormItem>
              <FormLabel>Notification Channels</FormLabel>
              <div className="flex gap-6 mt-2">
                <FormField
                  control={form.control}
                  name="channels"
                  render={({ field }) => (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="channel-email"
                        checked={field.value?.includes("email")}
                        onCheckedChange={(checked) => {
                          const current = field.value || [];
                          if (checked) {
                            field.onChange([...current, "email"]);
                          } else {
                            field.onChange(
                              current.filter((c) => c !== "email")
                            );
                          }
                        }}
                      />
                      <Label
                        htmlFor="channel-email"
                        className="font-normal cursor-pointer"
                      >
                        Email
                      </Label>
                    </div>
                  )}
                />
                <FormField
                  control={form.control}
                  name="channels"
                  render={({ field }) => (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="channel-inapp"
                        checked={field.value?.includes("in_app")}
                        onCheckedChange={(checked) => {
                          const current = field.value || [];
                          if (checked) {
                            field.onChange([...current, "in_app"]);
                          } else {
                            field.onChange(
                              current.filter((c) => c !== "in_app")
                            );
                          }
                        }}
                      />
                      <Label
                        htmlFor="channel-inapp"
                        className="font-normal cursor-pointer"
                      >
                        In App
                      </Label>
                    </div>
                  )}
                />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Alert Summary */}
        {selectedVariableName &&
          selectedOperator &&
          thresholdValue !== undefined && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
              <p className="text-sm text-muted-foreground mb-1">
                Alert will trigger when:
              </p>
              <p className="text-sm font-medium text-foreground">
                <span className="text-primary">{selectedVariableName}</span>
                {selectedGeoNodeName && (
                  <span className="text-muted-foreground">
                    {" "}
                    in {selectedGeoNodeName}
                  </span>
                )}
                {" is "}
                <span className="font-mono text-primary">
                  {
                    selectedOperator}
                </span>{" "}
                <span className="font-mono font-bold text-primary">
                  {thresholdValue.toLocaleString()}
                </span>
              </p>
            </div>
          )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSaving}
              className="flex-1"
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            disabled={isSaving || discoveryLoading}
            className={onCancel ? "flex-1" : "w-full"}
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditMode ? "Saving..." : "Creating..."}
              </>
            ) : isEditMode ? (
              "Save Changes"
            ) : (
              "Create Alert"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}

export default NotificationConfigForm;
